import useSWR, { mutate } from "swr";
import { useFetcher } from "~/hooks/useFetcher";
import {
  type AccessLevelOption,
  type ShareCollaborators,
  type ShareGroup,
  type ShareUser,
} from "~/pages/manifold-nextgen/components/share/types";
import {
  ProjectShare,
  ShareLevel,
  SharePrincipalType,
  fetchProjectSharesBatch,
  projectSharesBatchPath,
  projectSharesPath,
  setProjectShare,
} from "~/services/projects";

// Batch-shares SWR keys are `[batchPath, idsKey]` tuples; match any of them so a
// share change refreshes the ProjectCard avatars regardless of which page's ids
// they were fetched for.
const BATCH_SHARES_PATH = projectSharesBatchPath();
const isProjectSharesBatchKey = (key: unknown): boolean =>
  Array.isArray(key) && key[0] === BATCH_SHARES_PATH;

const accessLevels: Record<string, AccessLevelOption> = {
  [ShareLevel.OWNER]: {
    label: "Owner",
    description: "Full access, including managing agents, datasets, and sharing",
  },
  [ShareLevel.USER]: {
    label: "User",
    description: "Can use the project and its agents",
  },
};

const shareFullName = (share: ProjectShare): string =>
  [share.given_name, share.family_name].filter(Boolean).join(" ") || share.principal_id;

// The backend returns a flat list of shares; ShareModal wants users and groups
// split out, each carrying its access level as a plain string.
const toCollaborators = (shares: ProjectShare[]): ShareCollaborators => {
  const users: ShareUser[] = [];
  const groups: ShareGroup[] = [];
  for (const share of shares) {
    if (share.principal_type === SharePrincipalType.USER_TAG) {
      groups.push({
        name: share.principal_id,
        display_name: shareFullName(share),
        is_internal: false,
        access_level: share.level,
        users: [],
      });
    } else {
      users.push({
        email: share.principal_id,
        full_name: shareFullName(share),
        image_url: share.image_url ?? undefined,
        access_level: share.level,
      });
    }
  }
  return { users, groups };
};

export const useProjectShare = (projectId: string) => {
  const {
    data: shares,
    isLoading,
    error: collaboratorsError,
  } = useFetcher<ProjectShare[]>(projectSharesPath(projectId));

  const collaborators = shares ? toCollaborators(shares) : undefined;

  const handleShare = async (
    recipient: { user?: ShareUser; group?: ShareGroup },
    accessLevel: string | null
  ) => {
    await setProjectShare(projectId, {
      ...(recipient.user && { email: recipient.user.email }),
      ...(recipient.group && { group: recipient.group.name }),
      level: accessLevel as ShareLevel | null,
    });
    // Refresh this project's shares (settings) and the batch-fetched shares
    // backing the ProjectCard avatars, both now stale.
    await mutate(projectSharesPath(projectId));
    await mutate(isProjectSharesBatchKey);
  };

  return {
    shares,
    collaborators,
    isLoading,
    collaboratorsError,
    handleShare,
    accessLevels,
    defaultAccessLevel: ShareLevel.USER as string,
  };
};

// Second-phase loader for a page of project cards: returns a `{ id -> shares }`
// lookup once resolved, or `undefined` while loading. Keyed on the sorted ids so
// reordering or client-side filtering doesn't trigger a refetch. A failure here
// leaves avatars empty without disturbing the already-rendered list.
export const useProjectSharesBatch = (
  projectIds: string[]
): Record<string, ProjectShare[]> | undefined => {
  const idsKey = [...projectIds].sort().join(",");
  const { data, error } = useSWR(projectIds.length ? [BATCH_SHARES_PATH, idsKey] : null, () =>
    fetchProjectSharesBatch(projectIds)
  );
  // Resolve to an empty lookup on failure so cards drop the skeleton and render
  // without avatars, rather than pulsing forever waiting on data that won't come.
  if (error) {
    return {};
  }
  return data?.shares_by_project;
};
